import { useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import icon from '../assets/icone-cor-quadrado.svg';
import { navItemFor } from '../nav';
import { readPref, writePref } from '../lib/storage';
import { useTheme } from '../lib/theme';
import { useIsPhone } from '../lib/use-media';
import { AlertsList, BellCount, useAlerts } from './Alerts';
import { BottomBar } from './BottomBar';
import { SEARCH_EMPTY_HINT, SearchRows, useGlobalSearch } from './GlobalSearch';
import { Icon } from './Icon';
import { Sheet } from './Overlays';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

function titleFor(pathname: string): string {
  if (pathname === '/mais') return 'Mais';
  return navItemFor(pathname)?.label ?? 'Deizi3d';
}

/** Shell-Celular search sheet: input, "Tente" chips, results, empty state. */
function PhoneSearch({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const search = useGlobalSearch(q);
  const [year] = useState(() => new Date().getFullYear());
  return (
    <Sheet label="Buscar" onClose={onClose}>
      <div className="row" style={{ gap: 8 }}>
        <label className="search grow" style={{ maxWidth: 'none' }}>
          <Icon name="search" size={18} />
          <input
            aria-label="Buscar"
            placeholder="Produto, cliente, pedido, carretel…"
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- the sheet opens to type
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <button type="button" className="btn g" onClick={onClose}>
          Fechar
        </button>
      </div>
      {!q.trim() ? (
        <div className="col" style={{ gap: 8 }}>
          <span className="lbl">Tente</span>
          <div className="row wrap" style={{ gap: 6 }}>
            {['C-00', `P-${year}`, `Q-${year}`].map((t) => (
              <button key={t} type="button" className="chip" onClick={() => setQ(t)}>
                {t}
              </button>
            ))}
          </div>
        </div>
      ) : search.results.length ? (
        <div role="listbox" aria-label="Resultados" className="col">
          <SearchRows results={search.results} onPick={onClose} badgeWidth={84} />
        </div>
      ) : search.loading ? (
        <p className="sm muted">Buscando…</p>
      ) : (
        <div className="col" style={{ gap: 8, padding: '12px 0' }}>
          <h3>Nada encontrado para “{q.trim()}”</h3>
          <p className="sm muted">{SEARCH_EMPTY_HINT}</p>
          <button type="button" className="btn" onClick={() => setQ('')}>
            Limpar busca
          </button>
        </div>
      )}
    </Sheet>
  );
}

function PhoneTop() {
  const { pathname } = useLocation();
  const [sheet, setSheet] = useState<'search' | 'alerts' | null>(null);
  const alerts = useAlerts();
  const close = () => setSheet(null);
  return (
    <>
      <div className="ptop">
        <img src={icon} alt="Deizi3d" style={{ width: 36, height: 36, borderRadius: 9 }} />
        <h1 style={{ fontSize: 24 }} className="grow">
          {titleFor(pathname)}
        </h1>
        <button
          type="button"
          className="btn icon"
          aria-label="Buscar"
          onClick={() => setSheet('search')}
        >
          <Icon name="search" />
        </button>
        <button
          type="button"
          className="btn icon"
          aria-label={alerts.unread ? `Alertas, ${alerts.unread} novos` : 'Alertas'}
          onClick={() => setSheet('alerts')}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" />
          <BellCount unread={alerts.unread} />
        </button>
      </div>
      {sheet === 'search' ? <PhoneSearch onClose={close} /> : null}
      {sheet === 'alerts' ? (
        <Sheet label="Alertas" onClose={close}>
          <div
            className="menu"
            style={{ position: 'static', boxShadow: 'none', border: 0, padding: 0 }}
          >
            <AlertsList state={alerts} onPick={close} />
          </div>
          <button type="button" className="btn block" onClick={close}>
            Fechar
          </button>
        </Sheet>
      ) : null}
    </>
  );
}

/** Desktop: sidebar + top bar. Phone (< 768px): top bar with title + bottom bar. */
export function AppShell() {
  const { dz } = useTheme();
  const phone = useIsPhone();
  const [mini, setMini] = useState(() => readPref('sidebar') === 'mini');
  const setMiniPersist = (m: boolean) => {
    writePref('sidebar', m ? 'mini' : 'full');
    setMini(m);
  };

  if (phone) {
    return (
      <div className={`${dz} phone`}>
        <PhoneTop />
        <main className="pbody">
          <Outlet />
        </main>
        <BottomBar />
      </div>
    );
  }

  return (
    <div className={`${dz} app`}>
      <div className={mini ? 'sidecol mini' : 'sidecol'}>
        <Sidebar mini={mini} onToggleMini={() => setMiniPersist(!mini)} />
      </div>
      <div className="main">
        <Topbar mini={mini} onExpand={() => setMiniPersist(false)} />
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
